import { Box, Typography } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import { useParams } from 'react-router-dom';

import { MessageAttachment } from '../../../../../resources/discussion/types';

import {
    useGetTrackedTripProperties,
    useTrackEventAmplitude,
} from '../../../../../shared/analytics';

import { DossierId } from '../../../../../resources/dossier/types';

import { FileCard } from './FileCard';

type FilesSectionProps = {
    files: MessageAttachment[];
    getMessageInfo: (
        messageId?: number | null,
    ) => { authorName?: string; authorId?: string; sendDate?: Date } | undefined;
};

export const FilesSection = ({ files, getMessageInfo }: FilesSectionProps) => {
    const { dossierId } = useParams() as { dossierId: DossierId };
    const { formatMessage } = useIntl();

    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const onDownloadLinkClick = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.attachmentDownloaded({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    };

    if (files.length === 0) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: '600' }} color="text.secondary">
                {formatMessage({ id: 'discussion.attachments_list.files_subsection_title' })}
            </Typography>
            <Box
                component="ul"
                sx={{
                    m: 0,
                    p: 0,
                    listStyle: 'none',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    '& > li + li': {
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    },
                    '& > li:first-of-type': {
                        borderTopLeftRadius: 'inherit',
                        borderTopRightRadius: 'inherit',
                    },
                    '& > li:last-of-type': {
                        borderBottomLeftRadius: 'inherit',
                        borderBottomRightRadius: 'inherit',
                    },
                }}
            >
                {files.map((attachment) => (
                    <Box
                        component="li"
                        key={attachment.link}
                        sx={{ backgroundColor: 'background.variant' }}
                    >
                        <FileCard
                            attachment={attachment}
                            messageInfo={getMessageInfo(attachment.messageId)}
                            onDownloadLinkClick={onDownloadLinkClick}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
